// Eingabe ohne Maus (X10): Controller (Steam Deck, Standard-Belegung) und Tastatur lösen dieselben Aktionen aus.
// Die Maus läuft direkt über Pointer-Events in main.ts.

export type Action =
  | 'confirm' // A / Leertaste
  | 'back' // B / Rücktaste, Esc
  | 'start' // Start / Enter
  | 'prev' // LB / Q
  | 'next' // RB / E
  | 'stormDown' // LT / 1
  | 'stormUp' // RT / 2
  | 'reroll' // Y / R
  | 'slots' // X / F
  | 'retreat' // Back/Select / T
  // Fabi (Phase Kern): im Kampf das Tempo mit 1 bis 4 (0,5×, 1×, 2×, 4×); beim Aufstellen sind 1 und 2 weiter der Sturm
  | 'speed1'
  | 'speed2'
  | 'speed3'
  | 'speed4'
  // Phase Kern: zwischen Figuren und Kreisen umschalten (G)
  | 'figures'
  // Fabi (Phase Kern, 17.10.2026 nachts): Esc öffnet überall das Menü
  | 'menu';

const PAD_BUTTONS: [number, Action][] = [
  [0, 'confirm'],
  [1, 'back'],
  [2, 'slots'],
  [3, 'reroll'],
  [4, 'prev'],
  [5, 'next'],
  [6, 'stormDown'],
  [7, 'stormUp'],
  [8, 'retreat'],
  [9, 'start'],
  // Home-Taste des Controllers: das Menü
  [16, 'menu'],
];

const KEYS: Record<string, Action> = {
  ' ': 'confirm',
  Backspace: 'back',
  Escape: 'menu',
  Enter: 'start',
  q: 'prev',
  e: 'next',
  '1': 'speed1',
  '2': 'speed2',
  '3': 'speed3',
  '4': 'speed4',
  r: 'reroll',
  f: 'slots',
  t: 'retreat',
  g: 'figures',
};

const ARROWS: Record<string, [number, number]> = {
  ArrowLeft: [-1, 0],
  ArrowRight: [1, 0],
  ArrowUp: [0, -1],
  ArrowDown: [0, 1],
  a: [-1, 0],
  d: [1, 0],
  w: [0, -1],
  s: [0, 1],
};

export type InputState = {
  /** Bewegung des Cursors, -1..1 je Achse */
  move: { x: number; y: number };
  /** true, sobald ein Controller benutzt wurde; dann zeigt das Spiel Controller-Hinweise (X10). Die Maus setzt es zurück */
  usingPad: boolean;
  padConnected: boolean;
};

/** source: woher die Aktion kam; in Menüs drückt die Tastatur Knöpfe selbst, der Controller nicht */
export function createInput(onAction: (a: Action, source: 'key' | 'pad') => void): { poll: () => InputState; mouseUsed: () => void } {
  const held = new Set<string>();
  const prevPad = new Map<number, boolean>();
  const state: InputState = { move: { x: 0, y: 0 }, usingPad: false, padConnected: false };

  window.addEventListener('keydown', (ev) => {
    if (ev.target instanceof HTMLSelectElement || ev.target instanceof HTMLInputElement) return;
    const key = ev.key.length === 1 ? ev.key.toLowerCase() : ev.key;
    if (ARROWS[key]) {
      held.add(key);
      ev.preventDefault();
      return;
    }
    const a = KEYS[key];
    if (a && !ev.repeat) {
      ev.preventDefault();
      onAction(a, 'key');
    }
  });
  window.addEventListener('keyup', (ev) => {
    held.delete(ev.key.length === 1 ? ev.key.toLowerCase() : ev.key);
    // Die Leertaste drückt Knöpfe sonst beim Loslassen ein zweites Mal; das übernimmt die Aktion „bestätigen“
    if (ev.key === ' ' && !(ev.target instanceof HTMLInputElement || ev.target instanceof HTMLSelectElement)) ev.preventDefault();
  });
  window.addEventListener('blur', () => held.clear());

  function poll(): InputState {
    let x = 0;
    let y = 0;
    for (const k of held) {
      const d = ARROWS[k];
      if (d) {
        x += d[0];
        y += d[1];
      }
    }
    const pads = navigator.getGamepads ? navigator.getGamepads() : [];
    const pad = [...pads].find((p): p is Gamepad => !!p && p.connected);
    state.padConnected = !!pad;
    if (pad) {
      const ax = pad.axes[0] ?? 0;
      const ay = pad.axes[1] ?? 0;
      const dead = 0.25;
      if (Math.hypot(ax, ay) > dead) {
        x += ax;
        y += ay;
        state.usingPad = true;
      }
      const dpad: [number, number, number][] = [
        [12, 0, -1],
        [13, 0, 1],
        [14, -1, 0],
        [15, 1, 0],
      ];
      for (const [i, dx, dy] of dpad) {
        if (pad.buttons[i]?.pressed) {
          x += dx;
          y += dy;
          state.usingPad = true;
        }
      }
      for (const [i, a] of PAD_BUTTONS) {
        const pressed = !!pad.buttons[i]?.pressed;
        if (pressed && !prevPad.get(i)) {
          state.usingPad = true;
          onAction(a, 'pad');
        }
        prevPad.set(i, pressed);
      }
    }
    const len = Math.hypot(x, y);
    state.move = len > 1 ? { x: x / len, y: y / len } : { x, y };
    return state;
  }

  return {
    poll,
    mouseUsed: () => {
      state.usingPad = false;
    },
  };
}
